// 📁 stores/auth/useAuthStore.ts
import { defineStore } from 'pinia'
import { useNuxtApp } from '#app'
import type { User } from 'firebase/auth'
import type { Administrator } from '@farmfree/common/types/administrator/administrator'
import type { Company } from '@farmfree/common/types/company/company'
import type { CompanyType } from '@farmfree/common/types/company/companyType'
import { createAdministratorService } from '@/services/administrator/administratorService'
import { createCompanyService } from '@/services/company/companyService'
import { stopCompanyRealtimeWatcher } from '~/utils/watchCompanyRealtime'
import { clearAllCompanyCaches } from '~/utils/cache/companyCache'
// handleCompanyChange는 순환 참조 방지를 위해 동적 import 사용
import type { ApiResponse } from '@farmfree/common/types/apiResponse'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    initialized: false,
    currentUser: null as User | null,
    currentAdministrator: null as Administrator | null,
    currentCompany: null as Company | null,
    uiCompanyTypeOverride: null as CompanyType | null, // ✅ UI 미리보기용 오버라이드
  }),
  getters: {
    companyType: (state) =>
      state.uiCompanyTypeOverride ?? state.currentCompany?.companyType ?? 'basic',

    isLoggedIn: (state) => !!state.currentUser,
    user: (state) => state.currentUser,
    administrator: (state) => state.currentAdministrator,
    company: (state) => state.currentCompany,
    isDemoUser: (state) => state.currentAdministrator?.companyIds.length === 0,
  },
  actions: {
    // async login(email: string, password: string) {
    //   const nuxtApp = useNuxtApp()
    //   this.currentUser = await nuxtApp.$authService.login(email, password)
    // },

    async logout() {
      const nuxtApp = useNuxtApp()
      const companyId = this.currentCompany?.id

      console.log('[authStore] 로그아웃 시작, companyId:', companyId)

      // Store 메모리 초기화 (IndexedDB 삭제 전에 먼저 메모리 초기화)
      try {
        const { clearAllStores } = await import('~/utils/clearAllStores')
        await clearAllStores()
      } catch (e) {
        console.warn('[authStore] Store 메모리 초기화 실패:', e)
      }

      // IndexedDB 전체 삭제
      if (companyId) {
        try {
          const { getUnifiedDB } = await import('~/utils/db/unifiedIndexedDB')
          const db = getUnifiedDB()

          // 삭제 전 확인
          const before = await db.storeData.where({ companyId }).toArray()
          console.log('[authStore] 삭제 전 IndexedDB 데이터:', before.length, '개')

          await db.clearCompanyData(companyId)

          // 삭제 후 확인
          const after = await db.storeData.where({ companyId }).toArray()
          console.log('[authStore] 삭제 후 IndexedDB 데이터:', after.length, '개')
          console.log('[authStore] IndexedDB 캐시 삭제 완료:', companyId)
        } catch (e) {
          console.error('[authStore] IndexedDB 캐시 삭제 실패:', e)
        }
      } else {
        console.warn('[authStore] companyId가 없어서 IndexedDB 삭제 건너뜀')
      }

      // localStorage 캐시 정리
      try {
        clearAllCompanyCaches()

        // 메시지 캐시도 삭제 (sms_message_cache_{adminUid})
        const adminUid = this.currentUser?.uid
        if (adminUid) {
          localStorage.removeItem(`sms_message_cache_${adminUid}`)
          console.log('[authStore] 메시지 캐시 삭제 완료:', adminUid)
        }
      } catch (e) {
        console.warn('[authStore] localStorage 캐시 정리 실패:', e)
      }

      // 실시간 리스너 정리
      try {
        stopCompanyRealtimeWatcher()
      } catch (e) {
        console.warn('[authStore] 실시간 리스너 정리 실패:', e)
      }

      // Flutter 앱이면 저장된 Custom Token 삭제
      try {
        const { useFlutterBridge } = await import('~/composables/flutter/useFlutterBridge')
        const { isFlutterApp, clearCustomToken } = useFlutterBridge()
        if (isFlutterApp.value) {
          await clearCustomToken()
          console.log('[authStore] Flutter 앱 Custom Token 삭제 완료')
        }
      } catch (e) {
        console.warn('[authStore] Flutter Custom Token 삭제 실패:', e)
      }

      // Firebase 로그아웃
      await nuxtApp.$authService.logout()

      // 상태 초기화
      this.currentUser = null
      this.currentAdministrator = null
      this.currentCompany = null

      console.log('[authStore] 로그아웃 완료')
    },

    setFirebaseUser(user: User | null) {
      this.currentUser = user
      console.log('[authStore] Firebase User set:', user)
    },

    setAppUser(admin: Administrator) {
      this.currentAdministrator = admin
      console.log('[authStore] App User set:', admin)
    },

    setCompany(company: Company) {
      this.currentCompany = company
      console.log('[authStore] Company set:', company)
    },

    setCompanyTypeOverride(type: CompanyType | null) {
      this.uiCompanyTypeOverride = type
    },

    async updateCurrentCompany(updated: Company): Promise<ApiResponse<Company>> {
      try {
        const companyService = createCompanyService('admin')
        this.currentCompany = updated
        const response = await companyService.saveItem('', updated)

        if (response.isSuccess) {
          // this.setCompany(response.data!)
          // 저장이 완료되면 오버라이드를 제거 (실제 값이 반영됨)
          this.uiCompanyTypeOverride = null
        }

        return response
      } catch (error: any) {
        console.error('[authStore] updateCurrentCompany error:', error)
        return { isSuccess: false, message: error.message ?? '알 수 없는 오류' }
      }
    },

    /**
     * Flutter 앱 환경 정보 업데이트
     * 앱에서 OS, 앱 버전 등의 정보를 받아 Firestore에 저장
     */
    async updateAppEnvironmentIfFlutter(uid: string) {
      try {
        const { useFlutterBridge } = await import('~/composables/flutter/useFlutterBridge')
        const { isFlutterApp, requestAppEnvironment } = useFlutterBridge()

        if (!isFlutterApp.value) {
          return
        }

        console.log('[authStore] Flutter 앱 환경 정보 요청 중...')
        const appEnv = await requestAppEnvironment()

        if (appEnv) {
          console.log('[authStore] Flutter 앱 환경 정보 수신:', appEnv)

          // Firestore에 저장 (직접 API 호출)
          const { useApi } = await import('~/composables/useApi')
          await useApi().patch(`/api/administrator/${uid}/app-environment`, {
            appEnvironment: {
              ...appEnv,
              lastUpdatedAt: new Date().toISOString(),
            },
          })

          console.log('[authStore] Flutter 앱 환경 정보 저장 완료')
        } else {
          console.warn('[authStore] Flutter 앱 환경 정보 수신 실패')
        }
      } catch (e) {
        console.warn('[authStore] Flutter 앱 환경 정보 업데이트 실패:', e)
      }
    },

    async initializeAuth(): Promise<void> {
      const nuxtApp = useNuxtApp()
      this.initialized = false

      return new Promise((resolve) => {
        // onAuthStateChange 콜백
        nuxtApp.$authService.onAuthStateChange(async (firebaseUser: User | null) => {
          this.currentUser = firebaseUser
          console.log('[authStore] Firebase Auth state changed:', firebaseUser)

          if (firebaseUser) {
            try {
              const adminRes = await createAdministratorService().getById('', firebaseUser.uid)
              if (adminRes.isSuccess) {
                const admin = adminRes.data!

                if (!admin.lastSelectedDemoCompanyId) {
                  admin.lastSelectedDemoCompanyId = 'demoCompanyCafe'
                }

                this.setAppUser(admin)
                let companyId: string = admin.companyId

                if (admin.companyIds.length === 0) {
                  companyId = admin.lastSelectedDemoCompanyId
                }
                const companyRes = await createCompanyService('admin').getById('', companyId)
                if (companyRes!.isSuccess) {
                  this.setCompany(companyRes.data!)
                  // 순환 참조 방지를 위해 동적 import
                  const { handleCompanyChange } =
                    await import('~/composables/company/useCompanyChange')
                  handleCompanyChange(companyRes.data!.id)
                } else {
                  console.error('[authStore] Error fetching company:', companyRes.error)
                }

                // Flutter 앱이면 앱 환경 정보 업데이트 (비동기, 결과 대기 안 함)
                this.updateAppEnvironmentIfFlutter(firebaseUser.uid)
              }
            } catch (error: any) {
              console.error('[authStore] Error during user/company fetch:', error)
              this.currentAdministrator = null
              this.currentCompany = null
            }
          } else {
            // User signed out - 상태만 초기화 (logout()에서 이미 캐시 정리 완료)
            this.currentAdministrator = null
            this.currentCompany = null
          }

          // 모든 비동기 작업이 완료되면 resolve 호출
          this.initialized = true
          resolve()
        })
      })
    },
  },
})
